// SEQ-180 phase 2, task P17: the widget editor.
//
// A right-hand drawer, not a modal, so the live preview stays visible next to
// the form (plan section 7.1). Fields in the order the plan lists them: Name,
// Chart type, Metric, Secondary metric, Group by, Aggregation, Date range,
// Compare to, Filters, Sort, Limit. Save is disabled while validation fails,
// and every section 7.3 message renders inline under the field it belongs to,
// taken from the validator's own catalogue so message and rule cannot drift.
//
// Accessibility (plan section 7.4): the drawer traps focus, closes on Escape,
// and returns focus to whatever opened it. It is a dialog with an accessible
// name, and every control has a real label.
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isTopmostDialog, openDialogStack, pushDialog, removeDialog } from "../../lib/dialogStack";
import { X } from "lucide-react";
import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type {
  PnlAggregation,
  PnlChartType,
  PnlComparisonKind,
  PnlWidgetConfig,
  PnlWidgetGroupBy,
  PnlWidgetLayout,
} from "../../lib/pnl/pnlWidgetSchema";
import { monthOnMonthMetricKeys, permittedAggregationsFor } from "../../lib/pnl/pnlWidgetSchema";
import type { PnlMetricOption } from "../../features/pnlAnalytics/metricOption";
import type { PnlCustomDatePreset } from "../../features/pnlAnalytics/dashboardRequestKeys";
import type { PnlKeyView, PnlProjectNameSource } from "../../features/pnlAnalytics/widgetDataset";
import type { PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import {
  AGGREGATION_HINTS,
  AGGREGATION_LABELS,
  CHART_TYPE_OPTIONS,
  COMPARISON_OPTIONS,
  DASHBOARD_RANGE_VALUE,
  GROUP_BY_OPTIONS,
  groupByHint,
  applyDateRangeChoice,
  clampAggregation,
  dateRangeChoices,
  dateRangeValueOf,
  deriveDataGrain,
  findMetric,
  issueFor,
  monthOnMonthChartTypePatch,
  supportsSecondaryMetric,
  toggleMonthOnMonthMetric,
  validateDraft,
} from "../../features/pnlAnalytics/widgetEditorState";
import { MONTH_ON_MONTH_MAX_PROJECTS, projectIdsFromFilters, toggleProjectIdFilter } from "../../features/pnlAnalytics/monthOnMonth";
import { WIDGET_SIZE_OPTIONS, widgetSizeValue } from "../../features/pnlAnalytics/widgetSizes";
import { gridChartTypePatch } from "../../features/pnlAnalytics/gridEditorState";
import { PnlGridSettings } from "./PnlGridSettings";
import { isGridFieldError } from "../../features/pnlAnalytics/gridFieldErrors";
import { PnlMetricSelector } from "./PnlMetricSelector";
import { PnlWidgetRenderer } from "./PnlWidgetRenderer";
import { HowWorkedOutVisibility } from "../howWorkedOut/howWorkedOutVisibility";
import "./PnlWidgetEditor.css";

const PREVIEW_DEBOUNCE_MS = 250;

export interface PnlWidgetEditorDrawerProps {
  open: boolean;
  /** The config being edited, or a fresh draft for a new widget. */
  initialConfig: PnlWidgetConfig;
  initialLayout: PnlWidgetLayout;
  metrics: PnlMetricOption[];
  datePresets: PnlCustomDatePreset[];
  /** Rows already on the page for the widget's own grain, used for the preview without issuing another call. */
  previewRows: PnlAnalyticsRow[];
  previewView: PnlKeyView;
  queryFilters: PnlAnalyticsQueryFilters;
  /** SEQ-193. Every project's display name, for the month_on_month "group by project" mode's project picker. Optional so no other caller of this component needs to change. */
  projects?: PnlProjectNameSource[];
  saving: boolean;
  onClose: () => void;
  onSave: (config: PnlWidgetConfig, layout: PnlWidgetLayout) => void;
}

/**
 * The caller gives this component a `key` that changes per widget opened
 * (PnlAnalytics.tsx), so opening a different widget REMOUNTS the drawer and
 * the draft below is seeded from props at mount. That is deliberately not an
 * effect that re-seeds on prop change: a synchronous setState inside an
 * effect is a cascading render, and a remount also discards an abandoned
 * edit rather than resurrecting it when the same widget is reopened.
 */
export function PnlWidgetEditorDrawer({
  open,
  initialConfig,
  initialLayout,
  metrics,
  datePresets,
  previewView,
  queryFilters,
  projects,
  saving,
  onClose,
  onSave,
}: PnlWidgetEditorDrawerProps) {
  const [config, setConfig] = useState<PnlWidgetConfig>(initialConfig);
  const [layout, setLayout] = useState<PnlWidgetLayout>(initialLayout);
  const [previewConfig, setPreviewConfig] = useState<PnlWidgetConfig>(initialConfig);
  const drawerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const dialogId = useId();

  // The preview is debounced so typing a name does not re-render a chart on
  // every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setPreviewConfig(config), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [config]);

  // Always-latest callback ref (React review round 4). The effect below is
  // registered once per genuine open, so it must not close over a stale
  // `onClose`, and `onClose` must not be a reason to re-register it: every
  // caller passes an inline arrow, so naming it in the dependency array tore
  // the effect down and rebuilt it on every re-render of the page. That
  // restored focus to wherever it was when the drawer FIRST opened and then
  // re-grabbed it, which pulls focus out from under someone mid-edit. This
  // drawer re-renders constantly while a widget is edited, because the live
  // preview updates, so it was the worst place in the feature for it.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    // Joins the page-wide dialog stack (src/lib/dialogStack.ts) rather than
    // keeping a third independent trap. Without it, a Modal opening over this
    // drawer would leave both listeners live and one Escape would close both.
    pushDialog(openDialogStack, dialogId);

    openerRef.current = document.activeElement;
    const returnFocusTo = openerRef.current;

    // React review, HIGH: this used to record the opener and install the Tab
    // handler without ever MOVING focus in, which is the half that makes a
    // trap a trap. The drawer is portalled to document.body, so its node sits
    // after the rest of the app in document order: a keyboard user who opened
    // it with Enter kept focus on the now-hidden trigger and tabbed through
    // whatever followed in the body instead of into the dialog.
    //
    // Focus goes to the drawer container, not to the first field, so a screen
    // reader announces the dialog's own name before its first control, and so
    // the user is not dropped mid-form.
    const focusFrame = requestAnimationFrame(() => {
      drawerRef.current?.focus();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      // A dialog underneath another one ignores the keyboard entirely: the
      // one on top owns both Escape and the Tab trap.
      if (!isTopmostDialog(openDialogStack, dialogId)) return;
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      removeDialog(openDialogStack, dialogId);
      if (returnFocusTo instanceof HTMLElement) returnFocusTo.focus();
    };
    // `dialogId` is a useId value, stable for this instance's lifetime, so
    // the effect fires only on a real open or close. No suppression needed:
    // `onClose` is no longer named inside the body.
  }, [open, dialogId]);

  const patch = useCallback((changes: Partial<PnlWidgetConfig>) => {
    setConfig((previous) => ({ ...previous, ...changes }));
  }, []);

  const primaryDef = findMetric(metrics, config.metric);
  const secondaryDef = config.secondaryMetric ? findMetric(metrics, config.secondaryMetric) : undefined;
  // M4: the offer is the intersection of both metrics' permitted sets.
  const permitted = useMemo<readonly PnlAggregation[]>(
    () => (primaryDef ? permittedAggregationsFor(primaryDef, secondaryDef) : (["sum", "avg", "min", "max", "latest"] as const)),
    [primaryDef, secondaryDef],
  );

  const validation = useMemo(() => validateDraft(config, metrics), [config, metrics]);
  // Plan 7.1 lists a Filters field between Compare to and Sort. The config's
  // `filters` array is already in the schema and already narrows a widget
  // inside the page filter (dashboardRequestKeys.ts's resolveWidgetFilters),
  // so this exposes the one filter the RPC's request shape can actually
  // serve per widget: project status. Project ids stay on the filter bar,
  // where picking them from a list beats retyping them per widget.
  const statusFilterValue = String(
    config.filters.find((filter) => filter.field === "status")?.value ?? "",
  );
  const rangeChoices = useMemo(() => dateRangeChoices(datePresets), [datePresets]);
  const showSecondary = supportsSecondaryMetric(config.chartType);
  const lifetimeRange = config.dateRangeMode === "widget" && config.dateRange.preset === "project_lifetime";

  const handleMetricChange = (metricKey: string) => {
    const def = findMetric(metrics, metricKey);
    setConfig((previous) => ({
      ...previous,
      metric: metricKey,
      aggregation: clampAggregation(previous.aggregation, def, previous.secondaryMetric ? findMetric(metrics, previous.secondaryMetric) : undefined),
      dataGrain: deriveDataGrain(previous.groupBy, metricKey, metrics),
    }));
  };

  const handleGroupByChange = (groupBy: PnlWidgetGroupBy) => {
    setConfig((previous) => ({ ...previous, groupBy, dataGrain: deriveDataGrain(groupBy, previous.metric, metrics) }));
  };

  const applyStatusFilter = (previous: PnlWidgetConfig, status: string): PnlWidgetConfig => {
    const withoutStatus = previous.filters.filter((filter) => filter.field !== "status");
    return {
      ...previous,
      filters: status === "" ? withoutStatus : [...withoutStatus, { field: "status", op: "eq", value: status }],
    };
  };

  const handleChartTypeChange = (chartType: PnlChartType) => {
    setConfig((previous) => ({
      ...previous,
      chartType,
      // Dropping to a single-series chart drops the second metric with it,
      // rather than leaving an invisible field set on the saved config.
      secondaryMetric: supportsSecondaryMetric(chartType) ? previous.secondaryMetric : undefined,
      ...monthOnMonthChartTypePatch(previous, chartType),
      // SEQ-199: after the month on month patch, which clears `dataGrain`.
      ...gridChartTypePatch(previous, chartType),
    }));
  };

  // SEQ-199: a grid reads measures by rollup field, so the metric, secondary
  // metric, group by, aggregation, comparison, sort and limit controls do not
  // apply and are hidden rather than shown for fields that do nothing.
  const isGrid = config.chartType === "grid";
  // A grid setting that fails validation is explained under its own field. The validator's developer path ("grid.paretoBands ...")
  // never reaches the screen: the drawer shows one line beside Save instead.
  const visibleOtherErrors = validation.otherErrors.filter((error) => !isGridFieldError(error));
  const gridFieldsInvalid = isGrid && !validation.valid && validation.otherErrors.some(isGridFieldError);

  // SEQ-193
  const isMonthOnMonth = config.chartType === "month_on_month";
  const monthOnMonthRowsAreProjects = isMonthOnMonth && config.groupBy === "project";
  const selectedMonthOnMonthProjectIds = useMemo(() => projectIdsFromFilters(config.filters), [config.filters]);
  const sortedProjects = useMemo(
    () => [...(projects ?? [])].sort((a, b) => a.job_location.localeCompare(b.job_location)),
    [projects],
  );

  const handleToggleMonthOnMonthMetric = (metricKey: string) => {
    setConfig((previous) => toggleMonthOnMonthMetric(previous, metricKey));
  };

  const handleToggleMonthOnMonthProject = (projectId: string) => {
    setConfig((previous) => ({
      ...previous,
      filters: toggleProjectIdFilter(previous.filters, projectId, MONTH_ON_MONTH_MAX_PROJECTS),
    }));
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="pnl-drawer-scrim" onClick={onClose} />
      <div
        className="pnl-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit widget: ${initialConfig.name}`}
        ref={drawerRef}
        tabIndex={-1}
      >
        <div className="pnl-drawer-header">
          <span className="pnl-drawer-title">Widget settings</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close widget settings">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="pnl-drawer-body">
          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-name">
              Name
            </label>
            <input
              id="pnl-widget-name"
              type="text"
              className="form-input"
              value={config.name}
              maxLength={80}
              onChange={(e) => patch({ name: e.target.value })}
            />
            {issueFor(validation.issues, "name") && (
              <p className="pnl-field-error">{issueFor(validation.issues, "name")}</p>
            )}
          </div>

          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-chart-type">
              Chart type
            </label>
            <select
              id="pnl-widget-chart-type"
              className="form-input"
              value={config.chartType}
              onChange={(e) => handleChartTypeChange(e.target.value as PnlChartType)}
            >
              {CHART_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* SEQ-193: in "company" mode (rows are metrics) the plain Metric
              field is replaced by the checklist below, which is what keeps
              `metric`/`metrics` in sync (toggleMonthOnMonthMetric). In
              "project" mode it is still the one metric every project row is
              compared on, so it stays. */}
          {isGrid && <PnlGridSettings grid={config.grid} onChange={(grid) => patch({ grid })} />}

          {!isGrid && !(isMonthOnMonth && !monthOnMonthRowsAreProjects) && (
            <PnlMetricSelector
              label="Metric"
              value={config.metric}
              metrics={metrics}
              onChange={handleMetricChange}
              error={issueFor(validation.issues, "metric")}
            />
          )}

          {isMonthOnMonth && !monthOnMonthRowsAreProjects && (
            <div className="pnl-field">
              <span className="form-label">Metrics to compare</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {metrics.map((metric) => {
                  const checked = monthOnMonthMetricKeys(config).includes(metric.metricKey);
                  return (
                    <label key={metric.metricKey} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <input type="checkbox" checked={checked} onChange={() => handleToggleMonthOnMonthMetric(metric.metricKey)} />
                      {metric.displayName}
                    </label>
                  );
                })}
              </div>
              <p className="pnl-field-hint">Each one becomes a row, shown by calendar month. At least one is required.</p>
              {issueFor(validation.issues, "metric") && <p className="pnl-field-error">{issueFor(validation.issues, "metric")}</p>}
            </div>
          )}

          {showSecondary && !isGrid && (
            <PnlMetricSelector
              label="Secondary metric"
              value={config.secondaryMetric ?? ""}
              metrics={metrics}
              allowNone
              hint="Optional. Adds a second series, or a second axis on a scatter."
              error={issueFor(validation.issues, "secondaryMetric")}
              onChange={(metricKey) => patch({ secondaryMetric: metricKey === "" ? undefined : metricKey })}
            />
          )}

          {!isGrid && (
          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-group-by">
              Group by
            </label>
            <select
              id="pnl-widget-group-by"
              className="form-input"
              value={config.groupBy}
              onChange={(e) => handleGroupByChange(e.target.value as PnlWidgetGroupBy)}
            >
              {/* SEQ-193: a month_on_month table only ever models two row
                  shapes (its own metrics, or a hand-picked project shortlist),
                  so its Group by control offers only those two, read from the
                  same shared option list every other chart type uses. */}
              {(isMonthOnMonth ? GROUP_BY_OPTIONS.filter((o) => o.value === "company" || o.value === "project") : GROUP_BY_OPTIONS).map(
                (option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ),
              )}
            </select>
            {isMonthOnMonth && (
              <p className="pnl-field-hint">
                {monthOnMonthRowsAreProjects
                  ? "Rows are the projects picked below, for the one metric above."
                  : "Rows are the metrics checked above, added together across every project."}
              </p>
            )}

            {monthOnMonthRowsAreProjects && (
              <div style={{ marginTop: 10 }}>
                <span className="form-label">
                  Projects to compare ({selectedMonthOnMonthProjectIds.length}/{MONTH_ON_MONTH_MAX_PROJECTS})
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                  {sortedProjects.map((project) => {
                    const checked = selectedMonthOnMonthProjectIds.includes(project.id);
                    const disabled = !checked && selectedMonthOnMonthProjectIds.length >= MONTH_ON_MONTH_MAX_PROJECTS;
                    return (
                      <label
                        key={project.id}
                        style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, opacity: disabled ? 0.5 : 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => handleToggleMonthOnMonthProject(project.id)}
                        />
                        {project.job_location}
                      </label>
                    );
                  })}
                </div>
                <p className="pnl-field-hint">Pick 1 to {MONTH_ON_MONTH_MAX_PROJECTS} projects, each shown as its own row by month.</p>
              </div>
            )}
            {/* The hint is the SELECTED option's own, not one paragraph about
                two of the ten. Someone who has never used this page can now
                read what their choice does before they make it. month_on_month
                already carries its own, more specific hint above. */}
            {!isMonthOnMonth && <p className="pnl-field-hint">{groupByHint(config.groupBy)}</p>}
          </div>
          )}

          {/* SEQ-193: month_on_month reads each metric's own per-month bucket
              value straight off the fetch (see monthOnMonth.ts); it never
              folds several buckets into one the way every other chart type's
              Aggregation control governs, so the control is hidden rather
              than shown for a field that does nothing here. */}
          {!isMonthOnMonth && !isGrid && (
          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-aggregation">
              Aggregation
            </label>
            <select
              id="pnl-widget-aggregation"
              className="form-input"
              value={config.aggregation}
              onChange={(e) => patch({ aggregation: e.target.value as PnlAggregation })}
            >
              {permitted.map((aggregation) => (
                <option key={aggregation} value={aggregation}>
                  {AGGREGATION_LABELS[aggregation]}
                </option>
              ))}
            </select>
            {issueFor(validation.issues, "aggregation") ? (
              <p className="pnl-field-error">{issueFor(validation.issues, "aggregation")}</p>
            ) : (
              AGGREGATION_HINTS[config.aggregation] && (
                <p className="pnl-field-hint">{AGGREGATION_HINTS[config.aggregation]}</p>
              )
            )}
            {permitted.length === 1 && permitted[0] === "sum" && (
              <p className="pnl-field-hint">
                This chart reads each bar&apos;s total, so only Sum is offered. Average, Minimum, Maximum and Latest need a figure for each month, which this chart does not fetch.
              </p>
            )}
          </div>
          )}

          {isMonthOnMonth && (
            <div className="pnl-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={config.showChange ?? false}
                  onChange={(e) => patch({ showChange: e.target.checked })}
                />
                Show a Change column
              </label>
              <p className="pnl-field-hint">Adds the difference between the table's last two months, in dollars and percent.</p>
            </div>
          )}

          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-date-range">
              Date range
            </label>
            <select
              id="pnl-widget-date-range"
              className="form-input"
              value={dateRangeValueOf(config)}
              onChange={(e) => setConfig((previous) => applyDateRangeChoice(previous, e.target.value))}
            >
              {rangeChoices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            {dateRangeValueOf(config) === DASHBOARD_RANGE_VALUE && (
              <p className="pnl-field-hint">This widget follows the filter bar above the dashboard.</p>
            )}
            {issueFor(validation.issues, "dateRange") && (
              <p className="pnl-field-error">{issueFor(validation.issues, "dateRange")}</p>
            )}
          </div>

          {config.dateRangeMode === "widget" && config.dateRange.preset === "custom" && !config.dateRange.customPresetId && (
            <div style={{ display: "flex", gap: 10 }}>
              <div className="pnl-field" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="pnl-widget-from">
                  From month
                </label>
                <input
                  id="pnl-widget-from"
                  type="month"
                  className="form-input"
                  value={(config.dateRange.custom?.from ?? "").slice(0, 7)}
                  onChange={(e) =>
                    patch({
                      dateRange: {
                        preset: "custom",
                        custom: { from: e.target.value ? `${e.target.value}-01` : "", to: config.dateRange.custom?.to ?? "" },
                      },
                    })
                  }
                />
              </div>
              <div className="pnl-field" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="pnl-widget-to">
                  To month
                </label>
                <input
                  id="pnl-widget-to"
                  type="month"
                  className="form-input"
                  value={(config.dateRange.custom?.to ?? "").slice(0, 7)}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split("-").map(Number);
                    const lastDay = e.target.value ? new Date(year, month, 0).getDate() : 0;
                    patch({
                      dateRange: {
                        preset: "custom",
                        custom: {
                          from: config.dateRange.custom?.from ?? "",
                          to: e.target.value ? `${e.target.value}-${String(lastDay).padStart(2, "0")}` : "",
                        },
                      },
                    });
                  }}
                />
              </div>
            </div>
          )}

          {!isGrid && (
          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-comparison">
              Compare to
            </label>
            <select
              id="pnl-widget-comparison"
              className="form-input"
              value={config.comparison?.kind ?? ""}
              disabled={lifetimeRange}
              onChange={(e) =>
                patch({ comparison: e.target.value === "" ? undefined : { kind: e.target.value as PnlComparisonKind } })
              }
            >
              {COMPARISON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {lifetimeRange ? (
              <p className="pnl-field-hint">A lifetime range has no prior period to compare against.</p>
            ) : (
              issueFor(validation.issues, "comparison") && (
                <p className="pnl-field-error">{issueFor(validation.issues, "comparison")}</p>
              )
            )}
          </div>
          )}

          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-status-filter">
              Filters
            </label>
            <select
              id="pnl-widget-status-filter"
              className="form-input"
              value={statusFilterValue}
              onChange={(e) => setConfig((previous) => applyStatusFilter(previous, e.target.value))}
            >
              <option value="">No extra filter</option>
              <option value="active">Active projects only</option>
              <option value="upcoming">Upcoming projects only</option>
              <option value="completed">Completed projects only</option>
            </select>
            <p className="pnl-field-hint">
              Narrows this widget inside the dashboard filter above. It can only ever show less than the filter bar,
              never more.
            </p>
          </div>

          <div className="pnl-drawer-section-heading">Presentation</div>

          <div className="pnl-field">
            <label className="form-label" htmlFor="pnl-widget-size">
              Size
            </label>
            <select
              id="pnl-widget-size"
              className="form-input"
              value={widgetSizeValue(layout)}
              onChange={(e) => {
                const option = WIDGET_SIZE_OPTIONS.find((o) => o.value === e.target.value);
                if (option) setLayout(option.layout);
              }}
            >
              {WIDGET_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {!isGrid && (
          <div style={{ display: "flex", gap: 10 }}>
            <div className="pnl-field" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="pnl-widget-sort">
                Sort
              </label>
              <select
                id="pnl-widget-sort"
                className="form-input"
                value={config.sort ? config.sort.direction : ""}
                onChange={(e) =>
                  patch({
                    sort:
                      e.target.value === ""
                        ? undefined
                        : { by: config.metric, direction: e.target.value as "asc" | "desc" },
                  })
                }
              >
                <option value="">Unsorted</option>
                <option value="desc">Highest first</option>
                <option value="asc">Lowest first</option>
              </select>
            </div>
            <div className="pnl-field" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="pnl-widget-limit">
                Limit
              </label>
              <input data-seq-private
                id="pnl-widget-limit"
                type="number"
                min={1}
                className="form-input"
                placeholder="All"
                value={config.limit ?? ""}
                onChange={(e) => patch({ limit: e.target.value === "" ? undefined : Number(e.target.value) })}
              />
            </div>
          </div>
          )}

          <div className="pnl-drawer-section-heading">Preview</div>
          <div className="pnl-drawer-preview">
            {/* SEQ-240: no "How is this worked out" triggers in a draft preview inside the drawer. */}
            <HowWorkedOutVisibility.Provider value={false}>
              <PnlWidgetRenderer
                config={previewConfig}
                view={previewView}
                comparisonView={null}
                metrics={metrics}
                queryFilters={queryFilters}
                projects={projects}
              />
            </HowWorkedOutVisibility.Provider>
          </div>

          {visibleOtherErrors.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {visibleOtherErrors.map((error) => (
                <p className="pnl-field-error" key={error}>
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="pnl-drawer-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          {gridFieldsInvalid && (
            <span className="pnl-field-error pnl-drawer-footer-note" role="status">
              Fix the highlighted fields to save.
            </span>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!validation.valid || saving}
            onClick={() => onSave(config, layout)}
          >
            {saving ? "Saving…" : "Save widget"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
